/**
 * 🔒 Every label a printed sheet carries is Title Case (T402; decision 1060,
 * amending 0638), and every pill reads in Title Case whatever its text's
 * source (T427; decision 1110). The product owner, 2026-09-27: *"all titles
 * should use title case. Especially in customer facing documents"*, and
 * again, of the pill requirement specifically, *"the pill requirement is
 * title case"*. The totals box reads *Subtotal*, *Pennsylvania Sales Tax
 * (6.00%)* and *Total*, and every caption, column head, fact label and pill
 * on a page follows.
 *
 * 🔒 One function answers what Title Case is, so the renderer, the pill, the
 * string check and a test cannot come to disagree about a label:
 *
 * - every word opens on a capital;
 * - a joining word inside a label stays lower case (*Terms and Conditions*,
 *   *Amount in Words*, *Page 2 of 3*), the rule 0638 already set for a kind's
 *   name;
 * - a label's first word is capitalised whatever it is, and so is a last word
 *   that is a verb's particle, so a sheet reads *Bill To*, *Ship To* and
 *   *Created By* the way an invoice prints them;
 * - a word already carrying a capital past its first letter is somebody's
 *   spelling and is kept: *MFR*, *PO*, *SKU*, *CONFIDENTIAL*, *McAllen*;
 * - a placeholder such as `{date}`, a figure and a mark such as `#` are kept;
 * - a hyphenated word takes a capital after the hyphen (*Sign-In*, *Follow-Up*,
 *   *To-Dos*), and a unit in brackets such as `(ms)` stays as typed (decision
 *   1810, which carried the case to buttons, tile headings, column heads, tabs,
 *   dialog titles, menu entries and What's new).
 *
 * 🔨 It changes case and nothing else. No word is added, dropped or respelt,
 * which is why *Sub Total* became *Subtotal* in the string table and not here.
 *
 * 🔨 This module lives at the package's own top level rather than under
 * `print/` because a pill draws no printed sheet (decision 1110); `print/
 * titleCase.ts` re-exports these two names so the sheet's own imports keep
 * working unchanged.
 */

/** The joining words a label keeps lower case inside itself. */
const JOINING_WORDS: ReadonlySet<string> = new Set(['a', 'an', 'the', 'and', 'but', 'or', 'nor', 'for', 'of', 'on', 'in', 'to', 'by', 'at', 'as', 'per', 'via', 'vs', 'from', 'with', 'into']);

/** The joining words that end a label as a verb's particle or its preposition, and are capitalised there: *Bill To*, *Paid By*, *Ship Via*. */
const ENDING_PARTICLES: ReadonlySet<string> = new Set(['to', 'by', 'on', 'in', 'up', 'at', 'for', 'from', 'via', 'with']);

/** The units a label keeps as typed when they stand in brackets: *Median (ms)*. */
const BRACKET_UNITS: ReadonlySet<string> = new Set(['ms']);

/** A word's leading letter, past any opening mark such as a bracket or a quote; -1 where it has none. */
function firstLetterIndex(word: string): number {
  for (let index = 0; index < word.length; index += 1) {
    const character = word[index]!;
    if (character === '{') return -1;
    if (/\p{L}/u.test(character)) return index;
    if (/[\p{N}$#%]/u.test(character)) return -1;
  }
  return -1;
}

/** Whether a word carries a capital past its first letter, which makes it somebody's own spelling. */
function isOwnSpelling(word: string, letter: number): boolean {
  return /\p{Lu}/u.test(word.slice(letter + 1));
}

/** The bare word a joining-word test reads: lower case, its punctuation off. */
function bare(word: string): string {
  return word.toLowerCase().replace(/[^\p{L}]/gu, '');
}

/** 🔒 A label in Title Case, by the rule above. */
export function printTitleCase(label: string): string {
  const words = label.split(' ');
  // The words that carry a letter, so a trailing placeholder or mark does not count as the label's last word.
  const lettered = words.map((word, index) => (firstLetterIndex(word) >= 0 ? index : -1)).filter((index) => index >= 0);
  const firstWord = lettered[0];
  const lastWord = lettered[lettered.length - 1];
  return words
    .map((word, index) => {
      const letter = firstLetterIndex(word);
      if (letter < 0 || isOwnSpelling(word, letter)) return word;
      const plain = bare(word);
      if (word.startsWith('(') && BRACKET_UNITS.has(plain)) return word;
      const joining = JOINING_WORDS.has(plain);
      const keepLower = joining && index !== firstWord && !(index === lastWord && ENDING_PARTICLES.has(plain));
      const head = word.slice(0, letter);
      const rest = word.slice(letter + 1);
      const initial = word[letter]!;
      const capitalAfterHyphen = rest.replace(/(?<=\p{L})-(\p{Ll})/gu, (_match, letter: string) => `-${letter.toUpperCase()}`);
      return `${head}${keepLower ? initial.toLowerCase() : initial.toUpperCase()}${capitalAfterHyphen}`;
    })
    .join(' ');
}

/**
 * 🔒 Whether a string is a label the rule holds, rather than a sentence. A
 * sentence closes on a full stop, a question mark or an exclamation mark, or
 * carries a colon's clause or a semicolon, and is left to sentence case.
 */
export function printIsLabel(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === '') return false;
  return !/[.?!]$/.test(trimmed) && !/[;]/.test(trimmed) && !/:\s+\S/.test(trimmed);
}
